import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { useAppSelector } from '../../../../@types/redux';
import { PillButton, ControlledMuiAutoCompleteSelect, ControlledTextField } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import { ICommodityConditionFormValues } from '../../../../@types/model/masterData/commodityCondition/commodityConditionFormValues';
import CommodityConditionModelHelper from '../../../../@types/model/masterData/commodityCondition/commodityConditionModelHelper';
import { yupResolver } from '@hookform/resolvers/yup';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';
import OptionType from '../../../../@types/model/optionType';

interface ICommodityConditionFormProps {
    isLoading : boolean;
    initialValues : ICommodityConditionFormValues;
    onSubmit : (values : ICommodityConditionFormValues) => void;
    onClose : () => void;
    isMasterView : boolean;
}

const CommodityConditionForm = (props : ICommodityConditionFormProps) : React.ReactElement => {
    const commodities = useAppSelector(x => x.masterData.commodities);

    const { handleSubmit, control, formState: { isValid, isDirty }, trigger } = useForm<ICommodityConditionFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(CommodityConditionModelHelper.formSchema()),
        mode: 'onChange',
    });

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const commodityOptions = useMemo(() => {
        return commodities
            .filter(x => x.isActive)
            .map(OptionType.fromDataModel);
    }, [commodities]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div>
            <form onSubmit={handleSubmit(props.onSubmit)}>
                <div className={'fdc p20'}>
                    <ControlledTextField
                        className={'mb10'}
                        control={control}
                        label={'Condition'}
                        name={'condition'}
                        defaultValue={props.initialValues.condition}
                        multiline
                        rows={3}
                        size={'small'}
                    />
                    {props.isMasterView &&
                        <ControlledMuiAutoCompleteSelect
                            className={'p0 mb10 w500'}
                            control={control}
                            label={'Commodities'}
                            defaultValues={props.initialValues.commodities}
                            options={commodityOptions}
                            isMulti
                            name={'commodities'}
                        />
                    }
                    <div className={'flx1 fdr jcfs'}>
                        <ControlledLabelledCheckBox 
                            name={'isActive'}
                            label={'Is Active?'}
                            control={control}
                            defaultValue={props.initialValues.isActive}
                            disabled={props.initialValues.isActive}
                        />
                    </div>
                    <div className={'fdr flx1 aic jcfe mb20'}>
                        <Button 
                            className={'cpd mr20'}
                            onClick={props.onClose} 
                            type={'reset'}
                        >
                            CANCEL
                        </Button>
                        <PillButton
                            className={'pl30 pr30'}
                            text={'SAVE'}
                            size={'small'}
                            type={'submit'}
                            color={'secondary'}
                            disabled={ !isDirty || !isValid || props.isLoading }
                        />
                    </div>
                </div>
            </form>
            <CustomReactHookFormDevTools control={control} />
        </div>
    );
};

export default CommodityConditionForm;
